import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { CalendarDays, Clock, MapPin, Loader2 } from "lucide-react";
import { usePathname, navigate } from "wouter/use-browser-location";

const ARRIVAL_WINDOW_LABELS: Record<string, string> = {
  "9am-10am": "9:00am – 10:00am",
  "10am-11am": "10:00am – 11:00am",
  "11am-12pm": "11:00am – 12:00pm",
  "2pm-3pm": "2:00pm – 3:00pm",
  "3pm-4pm": "3:00pm – 4:00pm",
  "4pm-5pm": "4:00pm – 5:00pm",
};

function getWeekBucket(visitDate: Date): "this_week" | "next_week" | "future" | "past" {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayOfWeek = startOfToday.getDay(); // 0 = Sunday
  const startOfThisWeek = new Date(startOfToday);
  startOfThisWeek.setDate(startOfToday.getDate() - dayOfWeek);
  const endOfThisWeek = new Date(startOfThisWeek);
  endOfThisWeek.setDate(startOfThisWeek.getDate() + 7);
  const endOfNextWeek = new Date(endOfThisWeek);
  endOfNextWeek.setDate(endOfThisWeek.getDate() + 7);

  const d = new Date(visitDate.getFullYear(), visitDate.getMonth(), visitDate.getDate());
  if (d < startOfToday) return "past";
  if (d < endOfThisWeek) return "this_week";
  if (d < endOfNextWeek) return "next_week";
  return "future";
}

const BUCKET_LABELS: Record<string, string> = {
  this_week: "This Week",
  next_week: "Next Week",
  future: "Upcoming",
  past: "Past Visits",
};

const BUCKET_ORDER = ["this_week", "next_week", "future", "past"];

type VisitItem = {
  id: number;
  visitNumber: string;
  visitDate: Date;
  arrivalWindow?: string | null;
  customerName: string;
  customerAddress?: string | null;
  complaintDescription?: string | null;
  status: string;
  visitType: string;
};

function VisitCard({ visit, onClick }: { visit: VisitItem; onClick: () => void }) {
  const date = new Date(visit.visitDate);
  const dateStr = date.toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short" });
  const isToday = new Date().toDateString() === date.toDateString();

  return (
    <Card
      className={`cursor-pointer hover:shadow-sm transition-shadow ${isToday ? "border-black" : ""}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-12 text-center rounded-md py-1.5 ${isToday ? "bg-black text-white" : "bg-gray-100 text-gray-700"}`}>
            <p className="text-xs font-medium">{date.toLocaleDateString("en-SG", { weekday: "short" })}</p>
            <p className="text-lg font-bold leading-none mt-0.5">{date.getDate()}</p>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs font-semibold text-muted-foreground">{visit.visitNumber}</span>
              {visit.visitType === "urgent" && <span className="text-xs text-red-700 font-medium">🔴 Urgent</span>}
              {isToday && <span className="text-xs bg-black text-white px-1.5 py-0.5 rounded font-medium">Today</span>}
            </div>
            <p className="font-medium text-sm mt-0.5">{visit.customerName}</p>
            {visit.customerAddress && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                {visit.customerAddress}
              </p>
            )}
            {visit.arrivalWindow && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Clock className="h-3 w-3 flex-shrink-0" />
                {ARRIVAL_WINDOW_LABELS[visit.arrivalWindow] ?? visit.arrivalWindow}
              </p>
            )}
            {visit.complaintDescription && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{visit.complaintDescription}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TechnicianDashboard() {
  const pathname = usePathname();
  const { user } = useAuth();
  const { data: visits = [], isLoading } = trpc.siteVisits.myVisits.useQuery();

  // Group by week bucket
  const grouped: Record<string, VisitItem[]> = {};
  for (const v of visits) {
    const bucket = getWeekBucket(new Date(v.visitDate));
    if (!grouped[bucket]) grouped[bucket] = [];
    grouped[bucket].push(v as VisitItem);
  }

  // Sort each bucket by date ascending
  for (const bucket of Object.keys(grouped)) {
    grouped[bucket].sort((a, b) => new Date(a.visitDate).getTime() - new Date(b.visitDate).getTime());
  }

  const activeBuckets = BUCKET_ORDER.filter(b => grouped[b]?.length > 0);
  const todayCount = (grouped["this_week"] ?? []).filter(v => new Date(v.visitDate).toDateString() === new Date().toDateString()).length;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold">My Schedule</h1>
          <p className="text-sm text-muted-foreground">
            {todayCount > 0
              ? `${todayCount} visit${todayCount > 1 ? "s" : ""} today`
              : "No visits today"}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : activeBuckets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CalendarDays className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground font-medium">No visits assigned</p>
            <p className="text-sm text-muted-foreground/70 mt-1">You'll see your scheduled visits here</p>
          </div>
        ) : (
          activeBuckets.map((bucket) => (
            <div key={bucket}>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                {BUCKET_LABELS[bucket]}
                <span className="ml-2 text-xs font-normal normal-case">
                  ({grouped[bucket].length} visit{grouped[bucket].length > 1 ? "s" : ""})
                </span>
              </h2>
              <div className="space-y-2">
                {grouped[bucket].map((visit) => (
                  <VisitCard
                    key={visit.id}
                    visit={visit}
                    onClick={() => navigate(`/site-visits/id/${visit.id}`, { state: { from: pathname } })}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
